import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Menu, X, Home } from 'lucide-react';

// Maskotun etrafında dönen 3B ikonlar. Sistem üç sitede aynı; sadece bu dizi değişir.
// Sayı serbest — kaç ikon olursa olsun yörüngeye eşit aralıkla dağıtılır. Bu yüzden
// var olmayan bir dosyayı dizide bırakma: ikon gizlenir ama yerini korur, yörüngede
// boşluk kalır.
const ORBIT_ICONS = [
  { src: '/icons/3d/mic-dynamic-color.png' },
  { src: '/icons/3d/chat-text-dynamic-color.png' },
  { src: '/icons/3d/file-text-dynamic-color.png' },
];

// Şahin'e dokununca sırayla değişen küçük şakalar — kalıcı state gerekmiyor,
// sayfa yenilenince sıfırlanır.
const JOKES = [
  'Bu ödevin süresi de bu sayfa da geçmiş.',
  'Yoklama alındı: bu sayfa yok.',
  '404 — sınıf listesinde böyle bir sayfa yok.',
  'Bu sayfa devamsızlıktan kaldı.',
  'Öğretmen bu sayfayı ödev olarak vermemiş.',
  'Zil çaldı ama bu sayfa derse hiç girmedi.',
  'Sınıf panosunda böyle bir duyuru yok.',
];

interface NotFoundProps {
  /** Kullanıcının rolüne göre "eve dön" hedefi — öğretmen için /panel, öğrenci için /gorevlerim. */
  homePath: string;
  homeLabel: string;
  /** Menüde gösterilecek, role uygun kısayollar. */
  menuLinks: { to: string; label: string }[];
}

export default function NotFound({ homePath, homeLabel, menuLinks }: NotFoundProps) {
  const [jokeIdx, setJokeIdx] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <Helmet>
        <title>Sayfa Bulunamadı (404) — Bitmiyo Sınıf</title>
      </Helmet>

      <div className="relative min-h-screen overflow-hidden bg-slate-950">
        {/* Grid deseni */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:96px_96px]" />
        <div className="absolute top-1/3 left-1/4 w-[28rem] h-[28rem] bg-accent-500/15 rounded-full blur-[140px]" />

        {/* ── Minimal navbar — masaüstünde üstte, mobilde altta yüzer ───────── */}
        <div className="fixed md:absolute bottom-5 md:bottom-auto md:top-6 left-5 md:left-8 right-5 md:right-8 z-30 flex items-center justify-between pointer-events-none">
          <div className="relative pointer-events-auto">
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="flex items-center gap-2.5 pl-4 pr-5 py-2.5 rounded-full bg-white text-slate-900 font-black text-sm shadow-xl hover:scale-105 active:scale-95 transition-transform"
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
              Menü
            </button>

            {menuOpen && (
              <div className="absolute bottom-full md:bottom-auto md:top-full mb-3 md:mb-0 md:mt-3 left-0 w-56 bg-white rounded-2xl shadow-2xl p-2 animate-in fade-in zoom-in-95 duration-150">
                {menuLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="block px-4 py-2.5 rounded-xl text-sm font-bold text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to={homePath}
            aria-label={homeLabel}
            className="pointer-events-auto w-11 h-11 rounded-full bg-white text-slate-900 flex items-center justify-center shadow-xl hover:scale-105 active:scale-95 transition-transform"
          >
            <Home size={18} />
          </Link>
        </div>

        {/* ── İçerik ──────────────────────────────────────────────────────────── */}
        <div className="relative min-h-screen max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-center md:justify-between gap-8 md:gap-6 py-24 md:py-16">
          <div
            className="text-[7rem] sm:text-[10rem] md:text-[13rem] lg:text-[16rem] leading-[0.8] font-black text-white select-none shrink-0 origin-center md:origin-left"
            style={{ transform: 'scaleX(0.82)', letterSpacing: '-0.04em' }}
          >
            404
          </div>

          <div className="max-w-sm text-center md:text-left shrink-0">
            <p className="text-xl md:text-2xl font-bold text-white leading-snug">
              Hmm, burada bir şey yok. Seni rotana geri döndürelim.
            </p>
            <Link
              to={homePath}
              className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-accent-500 hover:bg-accent-400 text-slate-950 font-black text-sm transition-all active:scale-95"
            >
              {homeLabel}
            </Link>
          </div>

          {/* ── Sersemlemiş Şahin + uçuşan ders eşyaları ─────────────────────── */}
          <div className="orbit-stage relative shrink-0 w-64 h-64 md:w-80 md:h-80 flex items-center justify-center">
            {jokeIdx !== null && (
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:-top-4 md:-right-6 w-52 bg-white text-slate-900 text-xs font-bold rounded-2xl rounded-br-sm px-4 py-3 shadow-2xl z-40 animate-in fade-in zoom-in-95 duration-200">
                {JOKES[jokeIdx]}
              </div>
            )}

            {/* Yörüngedeki 3B ikonlar — üst yarıda maskotun arkasına geçerler.
                Animasyon/derinlik mantığı index.css'teki .orbit-* sınıflarında. */}
            {ORBIT_ICONS.map((icon, i) => (
              <span
                key={icon.src}
                className="orbit-item"
                // Yörüngeye eşit aralıkla dağılsınlar diye negatif gecikme.
                style={{ animationDelay: `${-(14 / ORBIT_ICONS.length) * i}s` }}
              >
                <img
                  src={icon.src}
                  alt=""
                  aria-hidden="true"
                  className="w-11 h-11 md:w-14 md:h-14 object-contain select-none pointer-events-none drop-shadow-lg"
                  onError={(e) => { (e.currentTarget.parentElement as HTMLElement).style.display = 'none'; }}
                />
              </span>
            ))}

            <button
              onClick={() => setJokeIdx((prev) => (prev === null ? 0 : (prev + 1) % JOKES.length))}
              aria-label="Şahin'e dokun"
              className="relative z-20 block cursor-pointer animate-[wobble_4s_ease-in-out_infinite] hover:animate-none"
            >
              <img
                src="/mascot-404.webp"
                alt="Bitmiyo Sınıf"
                className="h-56 w-auto md:h-72 object-contain drop-shadow-2xl hover:scale-105 active:scale-95 transition-transform"
              />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
